import { Clickable, Col, Icon, Row, Text } from 'components';
import { useInfiniteScroll } from 'core';
import { Api } from 'lib';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Header, TeamPanel } from 'screens/Admin/components';
import {
  bannedOptions,
  dummyOptions,
  Filters,
  List,
  sportOptions,
  statusOptions
} from './components';

/**
 * The whole visible state of this screen lives in the query string — the filters, the sort, which
 * team's panel is open, which of its tabs, and which participant is selected on the GloryBoard.
 *
 * The point is a link: an administrator looking at one fan's balance on one team can paste the
 * address to a colleague and have them land on the same thing, and a reload after an adjustment
 * comes back to where they were rather than to an unfiltered list.
 *
 *   /admin/teams?q=fc&sport=soccer&team=<id>&tab=gloryboard&participant=<userId>
 *
 * `new` is the one reserved value of `team`: it opens the create panel, which has no id of its own.
 */
const NEW_TEAM = 'new';

const SORT_KEYS = ['name', 'createdAt'];

/** The stored option object for a param value, falling back to the list's own "All". */
const optionFor = (options, param, key = 'value') =>
  options.find((option) => option[key] === param) || options[0];

let timer;

const Teams = ({}) => {
  const [searchParams, setSearchParams] = useSearchParams(),
    [loading, setLoading] = useState(true),
    { isMobile } = useSelector((state) => state.appState),
    dispatch = useDispatch(),
    { teamCount = 0 } = useSelector((state) => state?.admin) || {},
    fetching = useRef(false),
    skip = useRef(0),
    scrollId = 'admin-team-users-list';

  const updateParams = useCallback(
    (updates, { replace = false } = {}) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          Object.entries(updates).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') next.delete(key);
            else next.set(key, value);
          });

          return next;
        },
        { replace }
      ),
    [setSearchParams]
  );

  // Read as primitives, not as the objects the controls want: the fetch effect depends on these,
  // and an object rebuilt on every render would refire it forever.
  const textParam = searchParams.get('q') || '';
  const statusParam = searchParams.get('status') || '';
  const dummyParam = searchParams.get('dummy') || '';
  const bannedParam = searchParams.get('banned') || '';
  const sportParam = searchParams.get('sport') || '';
  const sortParam = SORT_KEYS.includes(searchParams.get('sort')) ? searchParams.get('sort') : 'name';
  const dirParam = searchParams.get('dir') === 'desc' ? -1 : 1;
  const teamParam = searchParams.get('team') || '';

  // The field is local so typing stays immediate; the query string catches up on the debounce
  // below. It re-syncs when `q` changes from somewhere else — the back button, or a pasted link.
  const [text, setText] = useState(textParam);

  useEffect(() => {
    setText(textParam);
  }, [textParam]);

  useEffect(() => {
    if (text === textParam) return;

    clearTimeout(timer);
    timer = setTimeout(() => updateParams({ q: text }, { replace: true }), 300);

    return () => clearTimeout(timer);
  }, [text, textParam, updateParams]);

  const type = useMemo(() => optionFor(statusOptions, statusParam), [statusParam]);
  const dummy = useMemo(() => optionFor(dummyOptions, dummyParam), [dummyParam]);
  const banned = useMemo(() => optionFor(bannedOptions, bannedParam), [bannedParam]);
  const sport = useMemo(() => optionFor(sportOptions, sportParam, 'opt'), [sportParam]);
  const sortBy = useMemo(() => ({ [sortParam]: dirParam }), [sortParam, dirParam]);

  // `?team=new` is the create panel; anything else is a team id.
  const dataId = teamParam === NEW_TEAM ? true : teamParam || null;

  const users = useSelector((state) =>
    state.admin?.teams?.filter((x) =>
      type.value === 'Pending approval' ? x.verified?.idProgress === 'pendingVerification' : true
    )
  );

  useInfiniteScroll(scrollId, getUsers);

  useEffect(() => {
    getUsers(true);
    getTeamCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textParam, statusParam, dummyParam, bannedParam, sportParam, sortParam, dirParam]);

  useEffect(() => {
    skip.current = users?.length || 0;
  }, [users]);

  // A different team is a different panel, so it opens where every panel opens — on Details, with
  // nobody selected. Only a link that names a tab arrives on one.
  const handleSelect = (userId) =>
    updateParams({ team: userId, tab: undefined, participant: undefined });

  const closePanel = () => updateParams({ team: undefined, tab: undefined, participant: undefined });

  const handleSort = (next) => {
    const [key, direction] = Object.entries(next)[0];
    updateParams({ sort: key, dir: direction === -1 ? 'desc' : 'asc' });
  };

  const setOption = (key) => (option) =>
    updateParams({ [key]: option?.value === 'All' ? undefined : option?.value });

  async function getTeamCount() {
    const res = await Api.post('/admin/countUsers', {
      pendingVerify: type.value === 'Pending approval',
      type: 'team',
      text: textParam,
      dummy: dummy.value === 'All' ? undefined : dummy.value === 'Yes',
      banned: banned.value === 'All' ? undefined : banned.value === 'Yes',
      sport: sport.value === 'All' ? undefined : sport.opt
    });

    if (res?.data.count !== undefined) {
      dispatch({
        type: 'ADMIN_SET_TEAM_COUNT',
        payload: res.data.count
      });
    }
  }

  async function getUsers(isFilterChange) {
    if (isFilterChange) {
      dispatch({
        type: 'ADMIN_SET_TEAMS',
        payload: null
      });
    }

    if (fetching.current) return;
    fetching.current = true;

    const res = await Api.post('/admin/getUsers', {
      pendingVerify: type.value === 'Pending approval',
      admin: false,
      type: 'team',
      skip: isFilterChange ? 0 : skip.current,
      text: textParam,
      sortBy,
      dummy: dummy.value === 'All' ? undefined : dummy.value === 'Yes',
      banned: banned.value === 'All' ? undefined : banned.value === 'Yes',
      sport: sport.value === 'All' ? undefined : sport.opt
    });

    if (!res) return (fetching.current = false);

    dispatch({
      type: 'ADMIN_ADD_TEAMS',
      payload: res
    });

    fetching.current = false;
    setLoading(false);
  }

  return (
    <Col noFlex wid="100%" ht="100vh" start>
      <Header>
        <Row center>
          <Text h3>Teams</Text>

          <Clickable onClick={() => updateParams({ team: NEW_TEAM, tab: undefined })}>
            <Icon marg="0 0 0 8px" type="add" dimensions={18} />
          </Clickable>

          <Row marg="0 0 0 30px" noFlex center>
            <Text h4 regular>
              Total teams:{' '}
            </Text>

            <Text h4 med marg="0 0 0 5px">
              {teamCount}
            </Text>
          </Row>
        </Row>
      </Header>

      <Col start noFlex marg="54px 0 0" wid="100%" ht="100vh" id={scrollId} scrollY>
        <Filters
          text={text}
          setText={setText}
          type={type}
          setType={setOption('status')}
          dummy={dummy}
          setDummy={setOption('dummy')}
          banned={banned}
          setBanned={setOption('banned')}
          sport={sport}
          setSport={(option) => updateParams({ sport: option?.opt })}
          className="filters"
        />

        <List
          onClick={handleSelect}
          dataId={dataId}
          data={users}
          onSort={handleSort}
          sortBy={sortBy}
          getUsers={getUsers}
          loading={loading}
        />
      </Col>

      <TeamPanel dataId={dataId} closeModal={closePanel} />
    </Col>
  );
};

export default Teams;
